/**
 * Format Error for Claude Code
 *
 * Generates markdown-formatted error reports optimized for copying to Claude Code
 * for debugging. Includes error details, stack trace, breadcrumbs, app context,
 * and instructions for systematic debugging.
 *
 * This enables the "copy to Claude" workflow from the Error Analytics Dashboard.
 *
 * @module lib/datahub/errorLogging/formatForClaude
 *
 * @example
 * ```typescript
 * import { formatErrorForClaude, copyErrorToClipboard } from './formatForClaude';
 *
 * function ErrorDetailsPanel({ error }) {
 *   const handleCopy = async () => {
 *     const success = await copyErrorToClipboard(error);
 *     if (success) {
 *       toast.success('Copied to clipboard! Paste in Claude Code.');
 *     }
 *   };
 *
 *   return <button onClick={handleCopy}>Copy for Claude</button>;
 * }
 * ```
 */

import type { Breadcrumb } from './types';

export interface ErrorForClaude {
  message: string;
  name: string;
  stack?: string;
  severity: string;
  category: string;
  source: string;
  file?: string;
  line?: number;
  column?: number;
  functionName?: string;
  componentName?: string;
  currentRoute?: string;
  userEmail?: string;
  connectionId?: string;
  entityType?: string;
  breadcrumbs?: Breadcrumb[];
  browser?: string;
  deviceType?: string;
  appVersion?: string;
  timestamp: string;
  fingerprint?: string;
  occurrenceCount?: number;
  additionalContext?: Record<string, unknown>;
}

/**
 * Format an error for copying to Claude Code
 *
 * Generates a comprehensive markdown-formatted error report with:
 * - Error summary with severity and category
 * - Full stack trace
 * - Source location (file, line, column, function)
 * - User action breadcrumbs leading to the error
 * - App context (route, user, connection)
 * - Environment details (browser, device, version)
 * - Instructions for Claude to systematically debug
 *
 * @param error - Error object with all captured metadata
 * @returns Markdown-formatted text ready to paste into Claude Code
 *
 * @example
 * ```typescript
 * const errorReport = formatErrorForClaude({
 *   message: 'Cannot read property "id" of undefined',
 *   name: 'TypeError',
 *   severity: 'error',
 *   category: 'uncaught_exception',
 *   source: 'client',
 *   file: 'ConnectionList.tsx',
 *   line: 45,
 *   breadcrumbs: [...],
 *   timestamp: '2026-01-26 3:45 PM'
 * });
 *
 * console.log(errorReport);
 * // # Systematically Debug This Error
 * // ...
 * ```
 */
export function formatErrorForClaude(error: ErrorForClaude): string {
  const lines: string[] = [];

  // Header
  lines.push('# Systematically Debug This Error');
  lines.push('');
  lines.push('I need help debugging an error in my React + Firebase application.');
  lines.push('');

  // Error Summary
  lines.push('## Error Summary');
  lines.push(`- **Type:** ${error.name}`);
  lines.push(`- **Message:** ${error.message}`);
  lines.push(`- **Severity:** ${error.severity}`);
  lines.push(`- **Category:** ${error.category.replace(/_/g, ' ')}`);
  lines.push(`- **Source:** ${error.source === 'client' ? 'Frontend' : 'Cloud Functions'}`);
  lines.push(`- **Occurred:** ${error.timestamp}`);
  if (error.occurrenceCount && error.occurrenceCount > 1) {
    lines.push(`- **Occurrences:** ${error.occurrenceCount}`);
  }
  lines.push('');

  // Stack Trace
  if (error.stack) {
    lines.push('## Stack Trace');
    lines.push('```');
    lines.push(error.stack);
    lines.push('```');
    lines.push('');
  }

  // Source Location
  if (error.file) {
    lines.push('## Source Location');
    lines.push(`- **File:** ${error.file}`);
    if (error.line) lines.push(`- **Line:** ${error.line}`);
    if (error.column) lines.push(`- **Column:** ${error.column}`);
    if (error.functionName) lines.push(`- **Function:** ${error.functionName}`);
    if (error.componentName) lines.push(`- **Component:** ${error.componentName}`);
    lines.push('');
  }

  // Breadcrumbs
  if (error.breadcrumbs && error.breadcrumbs.length > 0) {
    lines.push('## User Actions Before Error (Breadcrumbs)');

    // Format breadcrumbs with relative timestamps
    const errorTime = error.breadcrumbs[error.breadcrumbs.length - 1]?.timestamp || Date.now();
    const sortedBreadcrumbs = [...error.breadcrumbs].sort((a, b) => a.timestamp - b.timestamp);

    sortedBreadcrumbs.forEach((crumb, index) => {
      const timeAgo = formatTimeAgo(crumb.timestamp, errorTime);
      const typeIcon = getBreadcrumbIcon(crumb.type);
      lines.push(`${index + 1}. [${timeAgo}] ${typeIcon} ${crumb.category}: ${crumb.message}`);
    });

    lines.push(`${sortedBreadcrumbs.length + 1}. **ERROR OCCURRED**`);
    lines.push('');
  }

  // App Context
  lines.push('## App Context');
  if (error.currentRoute) lines.push(`- **Route:** ${error.currentRoute}`);
  if (error.connectionId) lines.push(`- **Connection ID:** ${error.connectionId}`);
  if (error.entityType) lines.push(`- **Entity Type:** ${error.entityType}`);
  if (error.userEmail) lines.push(`- **User:** ${error.userEmail}`);
  lines.push('');

  // Environment
  lines.push('## Environment');
  if (error.browser) lines.push(`- **Browser:** ${error.browser}`);
  if (error.deviceType) lines.push(`- **Device:** ${error.deviceType}`);
  if (error.appVersion) lines.push(`- **Build:** ${error.appVersion}`);
  lines.push('');

  // Additional Context
  if (error.additionalContext && Object.keys(error.additionalContext).length > 0) {
    lines.push('## Additional Context');
    lines.push('```json');
    lines.push(JSON.stringify(error.additionalContext, null, 2));
    lines.push('```');
    lines.push('');
  }

  // Instructions for Claude
  lines.push('---');
  lines.push('');
  lines.push('**Please:**');
  lines.push('1. Analyze the stack trace to identify the root cause');
  lines.push('2. Explain what likely triggered this error based on the breadcrumbs');
  lines.push('3. Suggest specific code changes to fix the issue');
  lines.push('4. Recommend defensive programming improvements');
  lines.push('');
  lines.push('The codebase is React 19 + Firebase + TanStack Query + Tailwind.');

  return lines.join('\n');
}

/**
 * Format timestamp as relative time
 */
function formatTimeAgo(timestamp: number, referenceTime: number): string {
  const diffMs = referenceTime - timestamp;
  const diffSec = Math.floor(diffMs / 1000);

  if (diffSec < 60) {
    return `${diffSec}s ago`;
  }

  const diffMin = Math.floor(diffSec / 60);
  const remainingSec = diffSec % 60;

  if (diffMin < 60) {
    return `${diffMin}m ${remainingSec}s ago`;
  }

  const diffHour = Math.floor(diffMin / 60);
  const remainingMin = diffMin % 60;

  return `${diffHour}h ${remainingMin}m ago`;
}

/**
 * Get icon for breadcrumb type
 */
function getBreadcrumbIcon(type: string): string {
  switch (type) {
    case 'navigation':
      return 'navigation';
    case 'click':
      return 'click';
    case 'api_call':
      return 'api_call';
    case 'api_response':
      return 'api_response';
    case 'state_change':
      return 'state';
    case 'console':
      return 'console';
    default:
      return 'action';
  }
}

/**
 * Copy formatted error to clipboard
 *
 * Formats an error and copies it to the system clipboard using the Clipboard API.
 * Falls back to execCommand for older browsers. Returns success status.
 *
 * @param error - Error object to format and copy
 * @returns Promise<boolean> - true if copy succeeded, false otherwise
 *
 * @example
 * ```typescript
 * const success = await copyErrorToClipboard(errorData);
 *
 * if (success) {
 *   alert('Copied! Paste in Claude Code to debug.');
 * } else {
 *   alert('Copy failed. Try selecting and copying manually.');
 * }
 * ```
 */
export async function copyErrorToClipboard(error: ErrorForClaude): Promise<boolean> {
  const formatted = formatErrorForClaude(error);
  const { safeWriteClipboard } = await import('../../utils/clipboard');
  return safeWriteClipboard(formatted);
}
